import { AnyAction } from "redux";
import { call, put, takeLatest } from "redux-saga/effects";
import { FILE_MANAGEMENT } from "./file-management.constants";
import {
  calculatePercentage,
  downloadStartedCall,
  fetchUserDetails,
  getFileList,
  getStorageConsumptionAction,
  resetToOriginalState,
  searchFetchCall,
  storageDetails,
} from "./file-management.action";
import {
  hideLoader,
  hideModel,
  showLoader,
  showModel,
  showToast,
} from "../../store/model/model.action";
import {
  cancelLargefiles,
  checkFileNameExist,
  committedChunks,
  deleteFiles,
  downloadFiles,
  getDeviceInstanceApi,
  getOrgDetails,
  getStorageConsumption,
  getUserPermission,
  searchFiles,
  siteIdStatic,
  uploadFile,
} from "./file-management.service";

import { SagaIterator } from "redux-saga";
import { queryStringToObject } from "../utils/helper";
import { getFormattedDateTime } from "../../../../../common/utility/dataManagementUtils";
import { FILE_MANAGEMENT_CONSTANTS } from "../utils/constant";
import { RetryCheckFileManagement } from "../popup/file-management-popup";

import { resetAbortController } from "../../common/interceptor/base.services";
import { store } from "../../../../../store";

export function* GetStorageConsumptionSaga(requestParams: any): SagaIterator {
  try {
    yield put(showLoader());
    const response = yield call(getStorageConsumption, requestParams.payload);
    yield put(hideLoader());
    if (response.status === 200) {
      const { message } = response.data;
      yield put(storageDetails(message));
    }
  } catch (error) {
    yield put(hideLoader());
  }
}

export function* getAllFiles(requestParams: any): SagaIterator {
  try {
    yield put(showLoader());
    const getFiles = yield call(
      getDeviceInstanceApi,
      queryStringToObject(requestParams?.payload),
    );
    yield put(hideLoader());
    if (getFiles.status === 200) {
      const { message } = getFiles.data;

      if (
        Array.isArray(message) &&
        message.length > 0 &&
        typeof message[0] === "object"
      ) {
        yield put(getStorageConsumptionAction(message[0]?.containerName));
        yield put(getFileList(message));
      } else {
        yield put(getFileList([]));
      }
    }
  } catch (error) {
    // Ensure loader is hidden for the current flow...
    yield put(hideLoader());
    // ...then rethrow so upstream callers (e.g. fileManagementListSaga) can handle/display the error
    throw error;
  }
}

export function* fileManagementListSaga(requestParams: any): SagaIterator {
  try {
    yield put(showLoader());
    yield call(getAllFiles, requestParams);
    yield put(hideLoader());
  } catch (error: any) {
    // Ensure loader is hidden and surface a useful error message to the user
    yield put(hideLoader());
    const message =
      (error && typeof error === "object" && error.message) ||
      "Failed to load file management list. Please try again.";
    yield put(showToast({ type: "error", content: message }));
  }
}

export function* resetSaga() {
  yield put({
    type: FILE_MANAGEMENT.ROW_KEY,
    payload: "",
  });
}

// Export UploadFileList for unit testing
export function* UploadFileList(action: AnyAction): SagaIterator {
  // try {
  try {
    const resolve = action?.meta?.resolve;
    const reject = action?.meta?.reject;
    const { fileManagementReducer } = store.getState();

    let totalBlockCount = action?.formData.get("BlockNumber");
    let getBlockNumber = action?.formData.get("totalBlockNumber");
    let parentPath = action?.parentPath || "";

    action?.formData.set(
      "RowKey",
      store.getState().fileManagementReducer.rowKey || "",
    );
    const response = yield call(uploadFile, action.formData);

    if (response.status === 200) {
      const { data } = response;
      yield put({
        type: FILE_MANAGEMENT.ROW_KEY,
        payload: data?.rowKey,
      });

      if (Number(totalBlockCount) === Number(getBlockNumber)) {
        const { rowKey, fileName, fileUploadType, totalFileSize } =
          response.data;
        let Payload = {
          filename: fileName,
          filesize: totalFileSize,
          fileuploadtype: fileUploadType,
          rowKey: rowKey,
          path: parentPath
        };
        const chunkResponse = yield call(committedChunks, Payload);
        yield put(calculatePercentage(0, "", 1));
        if (chunkResponse.status === 200) {
          if (resolve) resolve(); // ✅ Notify success
          const { data } = chunkResponse;
          yield put(
            showToast({ type: "success", content: `${fileName} Uploaded!` }),
          );
          yield put({ type: FILE_MANAGEMENT.RESET });
        } else {
          yield put({ type: FILE_MANAGEMENT.RESET });
          if (reject) reject();
        }
      } else {
        if (resolve) resolve(); // ✅ Notify success
      }
    } else {
      const { data } = response;
      yield put(hideLoader());
      yield put({ type: FILE_MANAGEMENT.RESET });
      yield put(calculatePercentage(0, "", 1));
    }
  } catch (error) {
    yield put(hideLoader());
    yield put({ type: FILE_MANAGEMENT.RESET });
  }
}

export function shortenFilename(filename: any) {
  // defensive guard to cover non-string inputs in tests/usage
  if (typeof filename !== "string") return filename || "";
  if (filename.length > 20) {
    return filename.slice(0, 17) + "...";
  }
  return filename;
}

export function* fileUploadedSaga(requestParams: AnyAction): SagaIterator {
  const { resolve, reject } = requestParams.meta || {};
  try {
    let getBlockNumber = requestParams.payload?.formData.get("BlockNumber");
    let totalBlockNumber =
      requestParams.payload?.formData.get("totalBlockNumber");
    let BlockNumber = requestParams.payload?.formData.get("BlockNumber");
    let fileName = requestParams.payload?.formData.get("FileName");
    var percentageValue = Number((BlockNumber / totalBlockNumber) * 100);

    if (getBlockNumber <= 1) {
      yield put(showLoader());
      const checkFileDuplicate = yield call(
        checkFileNameExist,
        queryStringToObject({
          filename: requestParams.payload?.fileName,
          parentPath: requestParams.payload?.parentPath,
        }),
      );
      const { data } = checkFileDuplicate || {};
      if (data?.statuscode === 200) {
        yield put(hideLoader());
        requestParams.payload?.formData.set("RowKey", data?.data?.rowKey);
        yield put({
          type: FILE_MANAGEMENT.ROW_KEY,
          payload: data?.data?.rowKey,
        });

        const popupProps = {
          title: "",
          description: FILE_MANAGEMENT_CONSTANTS.FILE_ALREADY_EXISTS,
          btnText1: FILE_MANAGEMENT_CONSTANTS.OK,
          // btnText: FILE_MANAGEMENT_CONSTANTS.CANCEL,
          onCancel: () => {
            store.dispatch(hideModel());
            store.dispatch({ type: FILE_MANAGEMENT.RESET });
            store.dispatch(calculatePercentage(0, "", 1));
            if (reject) reject(new Error("FILE_ALREADY_EXISTS"));
          },
          onTrigger: () => {
            store.dispatch(hideModel());
            store.dispatch({ type: FILE_MANAGEMENT.RESET });
            store.dispatch(calculatePercentage(0, "", 1));
            if (reject) reject(new Error("FILE_ALREADY_EXISTS"));
            // store.dispatch(
            //   calculatePercentage(1, shortenFilename(fileName), 1)
            // );
            // store.dispatch(hideModel());
            // store.dispatch({
            //   type: FILE_MANAGEMENT.RETRY_UPLOAD,
            //   formData: requestParams.payload?.formData,
            //   parentPath: requestParams.payload?.parentPath,
            //   meta: requestParams.meta,
            // });
          },
        };
        yield put(
          showModel({
            childTemplate: () => RetryCheckFileManagement(popupProps),
          }),
        );
      } else {
        yield put(
          calculatePercentage(
            percentageValue === 0 ? 1 : percentageValue,
            shortenFilename(fileName),
            percentageValue > 1 ? 1 : 0,
          ),
        );
        yield put(hideLoader());
        yield put({
          type: FILE_MANAGEMENT.RETRY_UPLOAD,
          formData: requestParams.payload?.formData,
          parentPath: requestParams.payload?.parentPath,
          meta: requestParams.meta,
        });
      }
    } else {
      yield put(
        calculatePercentage(
          percentageValue === 0 ? 1 : percentageValue,
          shortenFilename(fileName),
          percentageValue > 1 ? 1 : 0,
        ),
      );
      yield put({
        type: FILE_MANAGEMENT.RETRY_UPLOAD,
        formData: requestParams.payload?.formData,
        parentPath: requestParams.payload?.parentPath,
        meta: requestParams.meta,
      });
    }
  } catch (error) {
    yield put({ type: FILE_MANAGEMENT.RESET });

    yield put(hideLoader());
    if (reject) reject(error);
  }
}

export function* fileDeletedSaga(requestParams: AnyAction): SagaIterator {
  yield put(showLoader());
  const response = yield call(deleteFiles, requestParams.payload);
  if (response.status === 200) {
    const { message } = response.data;
    yield put(showToast({ type: "success", content: message }));
    let Payload = {
      payload: {
        siteId: siteIdStatic,
        parentPath: requestParams?.payload.path || "",
      },
    };

    yield call(getAllFiles, Payload);
    const { fileManagementReducer } = store.getState();
    if (fileManagementReducer.fullfileList?.length === 0) {
      yield put(getStorageConsumptionAction(requestParams?.payload?.container));
    }
  }
  yield put(hideLoader());
}

export function* searchFileSaga(requestParams: AnyAction): SagaIterator {
  yield put(showLoader());
  try {
    const response = yield call(searchFiles, requestParams.payload);
    if (response.status === 200) {
      const { message } = response.data;
      yield put(searchFetchCall(message));
    }
    yield put(hideLoader());
  } catch (error) {
    yield put(hideLoader());
  }
}

export function* downloadSelectedFileSaga(
  requestParams: AnyAction,
): SagaIterator {
  try {
    const response = yield call(downloadFiles, requestParams.payload);
    if (response.status === 200) {
      // const blob = response.data;
      const fileName = response.data?.fileName || "downloaded_file";
      const urlObject = response.data?.downloadUrl;
      //console.log("Download response:", response); // Debug log to inspect the response structure
      const link = document.createElement("a");
      link.href = urlObject;
      link.download = fileName;
      yield put(downloadStartedCall(false));
      yield put(resetToOriginalState());
      link.click();
      setTimeout(() => URL.revokeObjectURL(urlObject), 60 * 1000);
    } else {
      //console.log("Unexpected response status:", response.status, response); // Debug log for unexpected status
      yield put(
        showToast({
          type: "error",
          content:
            response.data?.error ||
            "Failed to download file. Please try again.",
        }),
      );
      yield put(downloadStartedCall(false));
      yield put(resetToOriginalState());
    }
  } catch (error) {
    // yield put(calculatePercentage(0, "", 0));
    yield put(downloadStartedCall(false));
    if (error && typeof error === "object" && "response" in error) {
      const { data } = error.response as {
        data: {
          title?: string;
          message?: string;
        };
      };
      yield put(
        showToast({ type: "error", content: data?.message || data?.title }),
      );
    } else {
      yield put(
        showToast({ type: "error", content: "An unexpected error occurred" }),
      );
    }
  }
}

export function* cancelUploadSaga(action: AnyAction): SagaIterator {
  try {
    resetAbortController();
    // Wrap in arrow so call receives a zero-arg function, avoiding TS overload mismatch
    const response = yield call(() => cancelLargefiles());

    if (response && response.status === 200) {
      yield put(calculatePercentage(0, "", 1));
      yield put({ type: FILE_MANAGEMENT.RESET });
      yield call(getAllFiles, { payload: action.payload });
    } else {
      yield put(
        showToast({
          type: "error",
          content: "An unexpected error occurred",
        }),
      );
    }
  } catch (error) {
    yield put(
      showToast({
        type: "error",
        content: "An unexpected error occurred",
      }),
    );
  }
}

export default function* watchAnalyticsLoad() {
  yield takeLatest(FILE_MANAGEMENT.RESET, resetSaga);
  yield takeLatest(FILE_MANAGEMENT.CANCEL_UPLOAD, cancelUploadSaga);
  yield takeLatest(FILE_MANAGEMENT.RETRY_UPLOAD, UploadFileList);
  yield takeLatest(FILE_MANAGEMENT.DOWNLOAD_LIST, downloadSelectedFileSaga);
  yield takeLatest(
    FILE_MANAGEMENT.DELETE_FILE_MANAGEMENT_FOLDER,
    fileDeletedSaga,
  );
  yield takeLatest(FILE_MANAGEMENT.GET_STORAGE_DATA, GetStorageConsumptionSaga);
  yield takeLatest(FILE_MANAGEMENT.GET_ALL_FILES, getAllFiles);
  yield takeLatest(FILE_MANAGEMENT.UPLOAD_FILE_MANAGEMENT, fileUploadedSaga);
  yield takeLatest(FILE_MANAGEMENT.SEARCH_FILTER, searchFileSaga);
  yield takeLatest(
    FILE_MANAGEMENT.FETCH_FILE_MANAGEMENT_LIST,
    fileManagementListSaga,
  );
}
